/**
 * The temporary "override for Admins" (N5, Pedro 2026-10-25): "make the behaviour for Admins the
 * same as for everyone else but temporarily overlay an "override for Admins" option, to facilitate
 * Beta testing but in a realistic way". Off by default; while on, an Admin may save over or delete
 * a locked trade and clear Sent for execution / Executed. Kept for this browser tab only
 * (sessionStorage), shared by the Trades lists and trade mode; the server honours it for Admins
 * only (`admin_override=true`).
 */
import { useSyncExternalStore } from 'react';
import { Switch, Tooltip } from '@mantine/core';

import { useCurrentUser } from './useAuth';

const KEY = 'baluarte.adminOverride';
const listeners = new Set<() => void>();
const read = (): boolean => {
  try { return sessionStorage.getItem(KEY) === '1'; } catch { return false; }
};
let value = read();
const set = (v: boolean) => {
  value = v;
  try { if (v) sessionStorage.setItem(KEY, '1'); else sessionStorage.removeItem(KEY); } catch { /* private mode */ }
  listeners.forEach((l) => l());
};

/** Whether the override is on AND the user is an Admin (anyone else: always false). */
export function useAdminOverride(): boolean {
  const on = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => value);
  const { user } = useCurrentUser();
  return on && user?.role === 'admin';
}

/** The switch, shown to Admins only. */
export function AdminOverrideSwitch() {
  const { user } = useCurrentUser();
  const on = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => value);
  if (user?.role !== 'admin') return null;
  return (
    <Tooltip withArrow multiline w={320}
      label="Beta testing only: lets you, as an Admin, change or delete in trade mode a trade that is no longer a Draft; every use is written to the Audit log. Off: you follow the same rules as everyone. For this browser tab only.">
      <Switch size="xs" color="red" checked={on} onChange={(e) => set(e.currentTarget.checked)}
        label={<span style={{ color: on ? 'var(--mantine-color-red-7)' : undefined, fontWeight: on ? 700 : 400 }}>Admin override (Beta)</span>} />
    </Tooltip>
  );
}
